// "Orbital character" tab of the set-up step: the DFT orbital projections of the bands selected for the fit at one
// k-point (a high-symmetry point of the path, or the path k-point nearest to a given k).
import { Alert, Badge, Group, NumberInput, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { PALETTE, Plot } from "../../components/Plot";
import { characterFigure, characterView, kLabel, type Cfg, type CharacterView } from "../../lib/skfitter";

export function CharacterTab({ jobId, cfg }: { jobId: string; cfg: Cfg }) {
  const [mode, setMode] = useState<"point" | "k">("point");
  const [point, setPoint] = useState<string | null>(null);
  const [k, setK] = useState<[number, number, number]>([0, 0, 0]);
  const sel = mode === "k" ? { k } : point ? { point } : {};
  const [debounced] = useDebouncedValue(JSON.stringify([cfg.n_bands_below, cfg.n_bands_above, cfg.include_soc, sel]), 300);
  const q = useQuery({
    queryKey: ["sk-character", jobId, debounced],
    queryFn: () => characterView(jobId, cfg, JSON.parse(debounced)[3]),
    placeholderData: (p) => p,
  });
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Text size="sm" c="dimmed">Reading the orbital projections …</Text>;
  const cv = q.data as CharacterView;
  const fig = cv.rows.length ? characterFigure(cv, PALETTE) : null;
  const height = Math.min(900, Math.max(300, 26 * cv.rows.length + 140));
  return (
    <Stack gap="xs" data-testid="character-tab">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <SegmentedControl size="xs" value={mode} onChange={(v) => setMode(v as "point" | "k")}
          data={[{ value: "point", label: "High-symmetry point" }, { value: "k", label: "k-point (kx, ky, kz)" }]} />
        {mode === "point" ? (
          <Select size="xs" w={170} label="Point" allowDeselect={false} value={point ?? cv.points[0]?.label ?? null}
            data={cv.points.map((p) => ({ value: p.label, label: `${kLabel(p.label)}  (${p.frac.map((v) => v.toFixed(3)).join(", ")})` }))}
            onChange={setPoint} />
        ) : (
          <Group gap={6}>
            {(["kx", "ky", "kz"] as const).map((n, i) => (
              <NumberInput key={n} size="xs" w={86} label={n} value={k[i]} step={0.05} decimalScale={4}
                onChange={(v) => { const nk = [...k] as [number, number, number]; nk[i] = typeof v === "number" ? v : 0; setK(nk); }} />
            ))}
          </Group>
        )}
      </Group>
      <Text size="xs" c="dimmed">kx, ky, kz in units of the reciprocal lattice vectors. The projections exist only on the DFT
        band path, so the nearest path k-point is shown. Bars: the states of the bands selected for the fit at this k, split
        by element and orbital family; grey: weight outside the projection basis.</Text>
      {cv.k && (
        <Group gap={6}>
          <Badge variant="light">{cv.k.label ? kLabel(cv.k.label) : "path k-point"} = ({cv.k.frac.map((v) => v.toFixed(3)).join(", ")})</Badge>
          <Badge variant="light" color="gray">{cv.rows.length} fitted states</Badge>
          {cv.k.asked && cv.k.distance > 1e-3 &&
            <Badge variant="light" color="orange">nearest path point, {cv.k.distance.toFixed(3)} Å⁻¹ from the k asked</Badge>}
        </Group>
      )}
      {cv.error && <Alert color="orange" p="xs"><Text size="xs">{cv.error}</Text></Alert>}
      {fig && <Plot data={fig.data} layout={fig.layout} height={height} />}
    </Stack>
  );
}
